import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { buttonClassName } from '@/components/ui/Button';
import { Card, Overline } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { Skeleton, SkeletonCard } from '@/components/ui/States';
import { Tag } from '@/components/ui/Badge';
import { daysLeft, formatDate, summariseAccess } from '@/features/billing/access-label';
import { useAccess } from '@/features/billing/use-access';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/account/billing')({
  validateSearch: (search: Record<string, unknown>): { paid?: boolean } =>
    search['paid'] === '1' || search['paid'] === 1 || search['paid'] === true ? { paid: true } : {},
  component: AccountBillingPage,
});

const NZD = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' });

function AccountBillingPage() {
  const { paid } = Route.useSearch();
  const { data: session, isPending } = authClient.useSession();
  const access = useAccess(Boolean(session));
  const payments = useQuery({
    queryKey: ['billing', 'payments'],
    enabled: Boolean(session),
    queryFn: () => api.get('/api/v1/billing/payments'),
  });

  if (isPending) {
    return <SkeletonCard />;
  }

  if (!session) {
    return (
      <Card padding="lg" className="grid max-w-auth gap-4">
        <h1 className="font-display text-h3">Log in to see your billing</h1>
        <Link to="/sign-in" className={buttonClassName()}>
          Log in
        </Link>
      </Card>
    );
  }

  const data = access.data;
  const summary = data ? summariseAccess(data) : null;

  return (
    <div className="grid max-w-prose gap-6">
      <PageHeader title="Billing" description="Your plan, add-ons and receipts." />

      {paid ? (
        <Alert tone="success" title="Thank you. Your payment went through.">
          Your access starts straight away. If it does not show below yet, refresh in a minute.
        </Alert>
      ) : null}

      <Card padding="lg" className="grid gap-4">
        <Overline>Your plan</Overline>
        {access.isPending || !summary || !data ? (
          <Skeleton className="h-8 w-48" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display text-h3">{summary.title}</span>
              {summary.tone === 'locked' ? <Tag tone="locked">No access</Tag> : null}
              {summary.tone === 'active' ? <Tag tone="variant">Active</Tag> : null}
              {summary.tone === 'trial' ? <Tag>Free trial</Tag> : null}
            </div>
            <p className="text-body text-ink-2">{summary.detail}</p>
            {data.state === 'active' && data.accessEndsAt ? (
              <p className="text-small text-muted">
                {daysLeft(data.accessEndsAt, Date.now())} days left. Buy again at any time and the
                new time is added to the end.
              </p>
            ) : null}
            {data.state === 'none' ? (
              <p className="text-body text-ink-2">
                You cannot start new tests until you buy a plan. Your history stays here.
              </p>
            ) : null}
          </>
        )}
        <div>
          <Link to="/pricing" className={buttonClassName({ variant: 'secondary' })}>
            {data?.state === 'active' ? 'Extend my plan' : 'See plans'}
          </Link>
        </div>
      </Card>

      {data && data.addons.length > 0 ? (
        <Card padding="lg" className="grid gap-3">
          <Overline>Add-ons</Overline>
          <ul className="grid gap-2">
            {data.addons.map((addon) => (
              <li key={addon.slug} className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold">{addon.name}</span>
                <span className="text-small text-muted">Ends {formatDate(addon.endsAt)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card padding="lg" className="grid gap-3">
        <Overline>Receipts</Overline>
        {payments.isPending ? (
          <Skeleton className="h-6 w-full" />
        ) : payments.isError ? (
          <Alert tone="danger" title="We could not load your payments." />
        ) : payments.data.payments.length === 0 ? (
          <p className="text-body text-muted">No payments yet.</p>
        ) : (
          <ul className="grid gap-3">
            {payments.data.payments.map((payment) => (
              <li key={payment.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <b>{[payment.planName, ...payment.addonNames].filter(Boolean).join(' + ')}</b>
                  <span className="block text-small text-muted">
                    {formatDate(payment.createdAt)} · {NZD.format(payment.amountNzdCents / 100)}
                  </span>
                </span>
                {payment.receiptUrl ? (
                  <a
                    href={payment.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-small font-bold text-primary hover:underline"
                  >
                    Receipt
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
